import { colors } from "../../../../styles/colors";
import type {
  Overview,
  ViewsTriggersSummary,
  DailyViewData,
} from "../../../../types/dashboard";

export const getGeneralSummaryCards = (
  overview?: Overview | null,
  overviewMonth?: ViewsTriggersSummary | null,
  overviewWeek?: ViewsTriggersSummary | null,
  overviewDay?: ViewsTriggersSummary | null,
  viewsMonth?: DailyViewData[],
  viewsWeek?: DailyViewData[],
  viewsDay?: DailyViewData[],
  overviewLoading?: boolean,
  monthlyLoading?: boolean,
  weeklyLoading?: boolean,
  dailyLoading?: boolean
) => {
  // Use real hourly data from API if available, otherwise show empty chart
  let todayChartData: { time: string; users: number; views: number }[] = [];

  // Get current hour in Israel timezone
  const israelTime = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Jerusalem",
  });
  const currentHour = new Date(israelTime).getHours();

  if (viewsDay && viewsDay.length > 0) {
    // Group hourly data by time periods
    const hourlyData: { [key: string]: { users: number; views: number } } = {};

    viewsDay.forEach((hourData) => {
      const date = new Date(hourData.date);
      const hour = date.getHours();
      const timeKey = `${hour.toString().padStart(2, "0")}:00`;

      if (!hourlyData[timeKey]) {
        hourlyData[timeKey] = { users: 0, views: 0 };
      }

      hourlyData[timeKey].users += hourData.joins;
      hourlyData[timeKey].views += hourData.views;
    });

    // Create array only for hours up to current hour, showing data where available
    todayChartData = Array.from({ length: currentHour + 1 }, (_, index) => {
      const hour = index;
      const timeKey = `${hour.toString().padStart(2, "0")}:00`;
      const data = hourlyData[timeKey] || { users: 0, views: 0 };

      return {
        time: timeKey,
        users: data.users,
        views: data.views,
      };
    });
  } else {
    // No data for today - show empty chart only for hours up to current hour
    todayChartData = Array.from({ length: currentHour + 1 }, (_, index) => {
      const hour = index;
      const timeKey = `${hour.toString().padStart(2, "0")}:00`;

      return {
        time: timeKey,
        users: 0,
        views: 0,
      };
    });
  }

  // Use real weekly data from API if available, otherwise show empty chart
  let weeklyChartData: { time: string; users: number; views: number }[] = [];
  const dayNames = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
  const currentDayOfWeek = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.

  if (viewsWeek && viewsWeek.length > 0) {
    // Group daily data by day of week
    const weeklyData: { [key: string]: { users: number; views: number } } = {};

    viewsWeek.forEach((dayData) => {
      const date = new Date(dayData.date);
      const dayOfWeek = date.getDay();
      const dayName = dayNames[dayOfWeek];

      if (!weeklyData[dayName]) {
        weeklyData[dayName] = { users: 0, views: 0 };
      }

      weeklyData[dayName].users += dayData.joins;
      weeklyData[dayName].views += dayData.views;
    });

    // Only show days up to current day of week
    const daysToShow = dayNames.slice(0, currentDayOfWeek + 1);
    weeklyChartData = daysToShow.map((dayName) => ({
      time: dayName,
      users: weeklyData[dayName]?.users || 0,
      views: weeklyData[dayName]?.views || 0,
    }));
  } else {
    // No data for this week - show empty chart for all days up to current day
    const daysToShow = dayNames.slice(0, currentDayOfWeek + 1);
    weeklyChartData = daysToShow.map((dayName) => ({
      time: dayName,
      users: 0,
      views: 0,
    }));
  }

  // Use real monthly data from API if available, otherwise show empty chart
  let monthlyChartData: { time: string; users: number; views: number }[] = [];

  // Get current day of month in Israel timezone
  const israelTimeForMonth = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Jerusalem",
  });
  const currentDate = new Date(israelTimeForMonth);
  const currentDay = currentDate.getDate();

  if (viewsMonth && viewsMonth.length > 0) {
    // Group daily data by day of month
    const dailyData: { [key: string]: { users: number; views: number } } = {};

    viewsMonth.forEach((dayData) => {
      const date = new Date(dayData.date);
      const dayOfMonth = date.getDate();
      const dayKey = dayOfMonth < 10 ? `0${dayOfMonth}` : `${dayOfMonth}`;

      if (!dailyData[dayKey]) {
        dailyData[dayKey] = { users: 0, views: 0 };
      }

      dailyData[dayKey].users += dayData.joins;
      dailyData[dayKey].views += dayData.views;
    });

    // Only show days up to current day of month
    const daysToShow = Array.from(
      { length: Math.max(currentDay, viewsMonth.length) },
      (_, index) => {
        const dayNumber = index + 1;
        const dayKey = dayNumber < 10 ? `0${dayNumber}` : `${dayNumber}`;
        const data = dailyData[dayKey] || { users: 0, views: 0 };

        return {
          time: dayKey,
          users: data.users,
          views: data.views,
        };
      }
    );

    monthlyChartData = daysToShow;
  } else {
    // No data for this month - show empty chart only for days up to current day
    monthlyChartData = Array.from({ length: currentDay }, (_, index) => {
      const dayNumber = index + 1;
      const dayKey = dayNumber < 10 ? `0${dayNumber}` : `${dayNumber}`;

      return {
        time: dayKey,
        users: 0,
        views: 0,
      };
    });
  }

  // Calculate summaries using real data when available
  const todaySummary = overviewDay
    ? {
        joins: overviewDay.joins,
        views: overviewDay.views,
      }
    : {
        joins:
          todayChartData.length > 0
            ? todayChartData.reduce((sum, hour) => sum + hour.users, 0)
            : 0,
        views:
          todayChartData.length > 0
            ? todayChartData.reduce((sum, hour) => sum + hour.views, 0)
            : 0,
      };

  const weeklySummary = overviewWeek
    ? {
        joins: overviewWeek.joins,
        views: overviewWeek.views,
      }
    : {
        joins:
          weeklyChartData.length > 0
            ? weeklyChartData.reduce((sum, day) => sum + day.users, 0)
            : 0,
        views:
          weeklyChartData.length > 0
            ? weeklyChartData.reduce((sum, day) => sum + day.views, 0)
            : 0,
      };

  const monthlySummary = overviewMonth
    ? {
        joins: overviewMonth.joins,
        views: overviewMonth.views,
      }
    : {
        joins:
          monthlyChartData.length > 0
            ? monthlyChartData.reduce((sum, week) => sum + week.users, 0)
            : 0,
        views:
          monthlyChartData.length > 0
            ? monthlyChartData.reduce((sum, week) => sum + week.views, 0)
            : 0,
      };

  return [
    {
      title: "היום",
      useBackground: true,
      showGraph: true,
      graphColor: colors.red,
      chartData: todayChartData,
      isLoading:
        dailyLoading && !overviewDay && (!viewsDay || viewsDay.length === 0),
      cards: [
        {
          title: "הצטרפו",
          value: todaySummary.joins,
          type: "users",
        },
        {
          title: "צפו",
          value: todaySummary.views,
          type: "views",
        },
      ],
    },
    {
      title: "השבוע",
      useBackground: true,
      showGraph: true,
      graphColor: colors.teal,
      chartData: weeklyChartData,
      isLoading:
        weeklyLoading &&
        !overviewWeek &&
        (!viewsWeek || viewsWeek.length === 0),
      cards: [
        {
          title: "הצטרפו",
          value: weeklySummary.joins,
          type: "users",
        },
        {
          title: "צפו",
          value: weeklySummary.views,
          type: "views",
        },
      ],
    },
    {
      title: "החודש",
      useBackground: true,
      showGraph: true,
      graphColor: colors.yellow,
      chartData: monthlyChartData,
      isLoading:
        monthlyLoading &&
        !overviewMonth &&
        (!viewsMonth || viewsMonth.length === 0),
      cards: [
        {
          title: "הצטרפו",
          value: monthlySummary.joins,
          type: "users",
        },
        {
          title: "צפו",
          value: monthlySummary.views,
          type: "views",
        },
      ],
    },
    {
      title: "מתחילת הפעילות",
      useBackground: false,
      showGraph: false,
      conversionRate: overview?.conversion_rate || 0,
      isLoading: overviewLoading && !overview,
      cards: [
        {
          title: "הצטרפו",
          value: overview?.joins || 0,
          type: "users",
        },
        {
          title: "צפו",
          value: overview?.views || 0,
          type: "views",
        },
      ],
    },
  ];
};
